import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, Download, Loader2, Printer, X } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { buildProjectDocumentBundle } from '@/lib/api/v2/projects'
import { bundleFileName } from '@/lib/projectDocuments'
import { printPdfFrame, saveObjectUrl } from '@/lib/printBlob'
import { showErrorToast } from '@/lib/toast'

interface DocumentBundleDialogProps {
  projectId: string
  projectName?: string | null
  /** Ids to print, already in print order; null = closed. */
  documentIds: string[] | null
  orderLabel: string
  onClose: () => void
}

type BundleState =
  | { status: 'preparing' }
  | { status: 'ready'; url: string; excludedCount: number }
  | { status: 'error'; message: string }

/**
 * Mount it with a `key` per request: state starts at "preparing" for every new selection.
 *
 * SOLY-DOC-002 — "Imprimer le dossier": the server merges the selection into ONE PDF (cover sheet
 * listing every document, then the documents in the table's order). It is shown here, same-origin,
 * so one click opens ONE print dialog for the whole physical folder.
 */
export function DocumentBundleDialog({ projectId, projectName, documentIds, orderLabel, onClose }: DocumentBundleDialogProps) {
  const open = documentIds !== null && documentIds.length > 0
  const [state, setState] = useState<BundleState>({ status: 'preparing' })
  const frameRef = useRef<HTMLIFrameElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const count = documentIds?.length ?? 0

  useEffect(() => {
    if (!open || !documentIds) return
    let cancelled = false
    let url: string | null = null
    buildProjectDocumentBundle(projectId, documentIds)
      .then(({ pdf, excludedCount }) => {
        url = URL.createObjectURL(pdf)
        if (cancelled) {
          URL.revokeObjectURL(url)
          return
        }
        setState({ status: 'ready', url, excludedCount })
      })
      .catch((e: unknown) => {
        if (cancelled) return
        const message = e instanceof Error && e.message ? e.message : 'Impossible de préparer le dossier'
        setState({ status: 'error', message })
      })
    return () => {
      cancelled = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [open, projectId, documentIds])

  const fileName = bundleFileName(projectName)

  const print = () => {
    if (!printPdfFrame(frameRef.current)) showErrorToast("Utilisez l'icône d'impression du lecteur PDF")
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="flex h-[94vh] w-[96vw] max-w-[96vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[min(1100px,96vw)]"
        data-testid="project-document-bundle-dialog"
        // Focus lands on "Fermer", inside the dialog: keyboard users never tab through the page behind,
        // and the arrow keys stay ours (the default would focus the first button, "Imprimer").
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          closeRef.current?.focus()
        }}
      >
        <div className="flex flex-wrap items-center gap-3 border-b px-4 py-2.5">
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-sm font-semibold">
              Dossier à imprimer — {count} document{count > 1 ? 's' : ''}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Un seul PDF : sommaire en page 1, puis les documents dans l'ordre affiché ({orderLabel.toLowerCase()}).
            </DialogDescription>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              onClick={print}
              disabled={state.status !== 'ready'}
              data-testid="project-document-bundle-print-btn"
            >
              <Printer className="h-4 w-4" />
              Imprimer
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => state.status === 'ready' && saveObjectUrl(state.url, fileName)}
              disabled={state.status !== 'ready'}
              data-testid="project-document-bundle-download-btn"
            >
              <Download className="h-4 w-4" />
              Télécharger
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onClose}
              ref={closeRef}
              aria-label="Fermer (Échap)"
              data-testid="project-document-bundle-close-btn"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {state.status === 'ready' && state.excludedCount > 0 && (
          <div
            className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900"
            data-testid="project-document-bundle-excluded-warning"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {state.excludedCount === 1
                ? "1 document n'a pas pu être inclus (format non pris en charge ou fichier protégé). Il est signalé en rouge dans le sommaire : imprimez-le séparément."
                : `${state.excludedCount} documents n'ont pas pu être inclus (format non pris en charge ou fichier protégé). Ils sont signalés en rouge dans le sommaire : imprimez-les séparément.`}
            </span>
          </div>
        )}

        <div className="relative min-h-0 flex-1 bg-slate-100">
          {state.status === 'preparing' && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground"
              data-testid="project-document-bundle-preparing"
            >
              <Loader2 className="h-5 w-5 animate-spin" />
              Préparation du dossier ({count} document{count > 1 ? 's' : ''})…
            </div>
          )}
          {state.status === 'error' && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center text-sm text-destructive"
              data-testid="project-document-bundle-error"
            >
              <AlertTriangle className="h-6 w-6" />
              {state.message}
            </div>
          )}
          {state.status === 'ready' && (
            <iframe
              ref={frameRef}
              src={state.url}
              title={fileName}
              className="h-full w-full border-0 bg-white"
              data-testid="project-document-bundle-pdf"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
